import { useNavigate } from "@tanstack/react-router";
import type { JSX, ReactNode } from "react";

import type { IntentName } from "@/design";

/** A typed in-app navigation target — same `{to, search}` shape
 * `navigate()` takes, so a destination can carry search params without
 * losing SPA navigation (mirrors `AuditLogNavigation` in
 * `features/inventory/lib/audit-log-link.ts`). */
export interface KpiCardTarget {
  to: string;
  search?: Record<string, unknown>;
}

// eslint-disable-next-line react-refresh/only-export-components -- static map shared with dashboard `Windows`; no HMR state.
export const INTENT_VALUE_CLASS: Record<IntentName, string> = {
  success: "text-[var(--intent-success-fg)]",
  warning: "text-[var(--intent-warning-fg)]",
  danger: "text-[var(--intent-danger-fg)]",
  info: "text-[var(--intent-info-fg)]",
  neutral: "text-[var(--text-primary)]",
};

interface KpiCardProps {
  label: string;
  children: ReactNode;
  /** One plain-language line under the value: what this number means. */
  caption?: string;
  /** Link-styled line at the bottom. Rendered as a button when `to` is set. */
  sub?: ReactNode;
  intent?: IntentName;
  /** Navigation target for `sub` — an SPA transition via `navigate()`,
   * not a plain `<a href>`, so query-string destinations (e.g.
   * `{ to: "/orders", search: { attention: "any" } }`) don't drop router
   * state with a full page reload. */
  to?: KpiCardTarget;
}

/**
 * One ops-dashboard KPI tile: eyebrow label, big value, a caption that
 * explains the value to a non-engineer, and an optional link line.
 */
export function KpiCard({
  label,
  children,
  caption,
  sub,
  intent = "neutral",
  to,
}: KpiCardProps): JSX.Element {
  const navigate = useNavigate();

  return (
    <div
      className="flex flex-col gap-1.5 rounded-[14px] border border-[var(--premium-border)] bg-[var(--surface-raised)] p-5 shadow-[var(--sh-card)]"
      // ponytail: test id kept from the dashboard so its tests don't churn.
      data-testid="dashboard-kpi"
    >
      <span className="text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]">
        {label}
      </span>
      <div className={`text-3xl font-extrabold tabular-nums ${INTENT_VALUE_CLASS[intent]}`}>
        {children}
      </div>
      {caption !== undefined && (
        <span className="text-xs text-[var(--text-subtle)]">{caption}</span>
      )}
      {sub !== undefined &&
        (to ? (
          <button
            type="button"
            onClick={() => void navigate(to)}
            className="mt-1 text-left text-xs font-semibold text-[var(--brand-600)] hover:underline"
          >
            {sub}
          </button>
        ) : (
          <span className="mt-1 text-xs text-[var(--text-subtle)]">{sub}</span>
        ))}
    </div>
  );
}
